/**
 * Country Agency Landing Pages — Wave 3
 * Final 5 countries to reach full BPO parity:
 * scandinavia, thailand, malaysia, russia, china
 */

import type { CountryAgencyData } from './country-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, React/Node.js, cloud-native deployment, and scaling to millions of users.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with React, TypeScript, and modern cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM/GPT integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, Kubernetes, monitoring, and 24/7 managed services.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with WCAG accessibility compliance.' },
];

const sharedKPIs = [
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '80%+', label: 'Test Coverage' },
  { value: '<2h', label: 'Response Time' },
  { value: 'ISO 27001', label: 'Certified' },
];

const sharedTimeline = [
  { week: 'W1', milestone: 'Discovery call, requirements analysis, and architecture design' },
  { week: 'W2', milestone: 'Team assembly, developer interviews, and environment setup' },
  { week: 'W3', milestone: 'Sprint 1 kickoff with daily standups and code reviews' },
  { week: 'W4', milestone: 'First deliverable, demo, and iteration feedback loop' },
];

const sharedRelated = [
  { label: 'Digital Studio Overview', href: '/services/digital-studio' },
  { label: 'BPO Services', href: '/services/bpo' },
  { label: 'Pricing Models', href: '/pricing' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Contact Us', href: '/contact' },
  { label: 'Industries We Serve', href: '/industries' },
];

function makeAgencyCountry(overrides: Partial<CountryAgencyData> & Pick<CountryAgencyData, 'slug' | 'countryName' | 'countryFlag' | 'timezone' | 'languages' | 'currency' | 'title' | 'description' | 'keywords' | 'heroTitle' | 'heroSubtitle' | 'whyTitle' | 'whyIntro' | 'whyReasons' | 'advantages' | 'pricingComparison' | 'industries' | 'faqs'>): CountryAgencyData {
  return {
    heroStats: [
      { value: '200+', label: 'Engineers' },
      { value: '60%', label: 'Cost Savings' },
      { value: '2 wks', label: 'Team Setup' },
      { value: '24/7', label: 'Support' },
    ],
    services: sharedServices,
    kpis: sharedKPIs,
    timelineSteps: sharedTimeline,
    relatedLinks: sharedRelated,
    ...overrides,
  };
}

export const COUNTRY_AGENCY_PAGES_WAVE3: Record<string, CountryAgencyData> = {

  scandinavia: makeAgencyCountry({
    slug: 'software-development-agency-for-scandinavia',
    countryName: 'Scandinavia',
    countryFlag: '🇸🇪🇩🇰🇳🇴',
    timezone: 'CET (GMT+1)',
    languages: ['Swedish', 'Danish', 'Norwegian', 'English', 'German', 'French'],
    currency: 'SEK / DKK / NOK / EUR',
    title: 'Software Development Agency Scandinavia 2026 | Nordic Nearshore – Call IT Dev',
    description: 'Software development agency for Scandinavia. Custom SaaS, web, mobile, AI. CET timezone, Nordic-speaking teams, 55% savings vs Stockholm/Copenhagen. Free pilot!',
    keywords: ['software development agency Scandinavia', 'IT outsourcing Scandinavia', 'nearshore development Nordic', 'Softwareentwicklung Skandinavien', 'web development Sweden Denmark Norway', 'SaaS development Nordic'],
    heroTitle: 'Software Development Agency for Scandinavia — Nordic-Quality at Nearshore Prices',
    heroSubtitle: 'Scandinavian companies: get enterprise engineering teams in the same CET timezone. 55% savings vs Stockholm/Copenhagen. React, Node.js, AI, cloud — free 2-week pilot.',
    heroStats: [
      { value: '200+', label: 'Engineers' },
      { value: 'CET', label: 'Timezone' },
      { value: '55%', label: 'Cost Savings' },
      { value: '€25/h', label: 'Starting Rate' },
    ],
    whyTitle: 'Why Scandinavian Companies Choose Call IT Dev',
    whyIntro: 'Scandinavia\'s high developer costs (€90-160/hr) and talent shortage make nearshore development in Morocco a strategic advantage. Same timezone, multilingual teams, EU-grade compliance.',
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Real-time collaboration with Stockholm, Copenhagen, Oslo. Daily standups 9-17h CET.' },
      { icon: '💰', title: '55% Cost Savings', description: '€25-40/hr vs €90-160/hr in Scandinavia. Enterprise quality at nearshore prices.' },
      { icon: '🗣️', title: 'Multilingual Teams', description: 'English, French, German, and Nordic language support. Technical documentation in any language.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001 certified, GDPR-aligned. Swedish/Danish data regulations fully supported.' },
      { icon: '✈️', title: '4h from Stockholm', description: 'Direct flights from Stockholm, Copenhagen, Oslo. Onsite sprints and team visits easy.' },
      { icon: '🚀', title: 'Fast Team Assembly', description: 'Dedicated team operational in 2 weeks. No 3-month Nordic recruitment cycle.' },
    ],
    advantages: [
      { title: 'Nordic Quality Standards', description: 'We match Scandinavian engineering excellence with rigorous code reviews, CI/CD, and 80%+ test coverage.' },
      { title: 'Timezone Alignment', description: 'GMT+1 = same as Stockholm, Copenhagen, Oslo. True real-time collaboration.' },
      { title: 'Cost Efficiency', description: '55% savings vs onshore Scandinavian agencies without quality compromise.' },
      { title: 'EU Compliance', description: 'GDPR, ISO 27001, and Nordic regulatory familiarity.' },
      { title: 'Full-Stack Capabilities', description: 'React, TypeScript, Node.js, Python, AWS, Azure, AI/ML — all in-house.' },
      { title: 'Flexible Engagement', description: 'Staff augmentation, dedicated teams, or fixed-price projects. Scale from 2 to 50+ devs.' },
    ],
    pricingComparison: { local: '€90-160', morocco: '€25-40', savings: '55%+' },
    industries: [
      { name: 'SaaS & Tech', description: 'Product engineering for Nordic SaaS companies from MVP to scale.' },
      { name: 'FinTech', description: 'PSD2-compliant payment platforms, trading systems, and banking applications.' },
      { name: 'Gaming', description: 'Game backends, player platforms, and live-ops for Nordic gaming studios.' },
      { name: 'E-commerce', description: 'Omnichannel retail platforms, marketplace solutions, and inventory management.' },
      { name: 'CleanTech & Energy', description: 'Smart grid, IoT platforms, and renewable energy management systems.' },
      { name: 'Healthcare', description: 'Patient portals, telemedicine, EHR integrations, GDPR/HIPAA compliance.' },
    ],
    faqs: [
      { question: 'Can you work in the same timezone as Scandinavian teams?', answer: 'Yes — Morocco operates in CET (GMT+1), identical to Stockholm, Copenhagen, and Oslo. Daily standups, pair programming, and real-time Slack communication work seamlessly.' },
      { question: 'How do costs compare to Swedish/Danish agencies?', answer: 'Senior developers in Scandinavia cost €90-160/hr. Our rate is €25-40/hr — a 55%+ saving with equivalent quality, CI/CD practices, and 80%+ test coverage.' },
      { question: 'Are you familiar with Nordic business culture?', answer: 'We serve multiple Scandinavian clients and understand flat hierarchies, consensus-driven decisions, and the emphasis on work-life balance and sustainability.' },
      { question: 'Do you support Swedish or Danish language requirements?', answer: 'Our teams communicate primarily in English, with French and German also available natively. For Swedish/Danish UI/UX, we work with native-speaker QA reviewers.' },
    ],
  }),

  thailand: makeAgencyCountry({
    slug: 'software-development-agency-for-thailand',
    countryName: 'Thailand',
    countryFlag: '🇹🇭',
    timezone: 'ICT (GMT+7)',
    languages: ['English', 'French', 'Arabic', 'Thai'],
    currency: 'THB / USD',
    title: 'Software Development Agency Thailand 2026 – Call IT Dev',
    description: 'Software development agency for Thai companies. EU & MENA expansion, GDPR-compliant, multilingual. From $25/hr. Free pilot!',
    keywords: ['software development agency Thailand', 'IT outsourcing Thailand', 'nearshore development Thailand', 'web development Thailand', 'app development Bangkok'],
    heroTitle: 'Software Development Agency for Thailand — EU & MENA Market Bridge',
    heroSubtitle: 'Thai companies going global: European engineering, GDPR compliance, 12+ languages. Follow-the-sun with CET timezone. From $25/hr.',
    whyTitle: 'Why Thai Companies Partner with Call IT Dev',
    whyIntro: 'Thailand\'s tech companies expanding to European and MENA markets need GDPR-compliant, multilingual engineering — Morocco delivers.',
    whyReasons: [
      { icon: '🌍', title: 'EU & MENA Access', description: 'Native French, German, Arabic, English for European and Middle Eastern markets.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'CET (GMT+1) complements ICT (GMT+7) for extended coverage.' },
      { icon: '💰', title: 'Competitive Rates', description: '$25-40/hr for EU-grade engineering. Quality premium over local alternatives.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001, GDPR-aligned — essential for EU market entry.' },
      { icon: '🗣️', title: 'Multilingual', description: '12+ European and MENA languages from one center.' },
      { icon: '🏆', title: 'Enterprise Quality', description: 'CI/CD, 80%+ test coverage, SonarQube, code reviews.' },
    ],
    advantages: [
      { title: 'European Expansion', description: 'Build EU-market-ready products with GDPR compliance from day one.' },
      { title: 'Extended Coverage', description: '6h timezone offset = more productive hours across EMEA.' },
      { title: 'Language Capabilities', description: 'French, German, Arabic, Spanish — languages scarce in Thailand.' },
      { title: 'Cost Effective', description: 'Competitive rates for enterprise-quality development.' },
      { title: 'Full-Stack', description: 'React, Node.js, Python, AWS, mobile, AI — all capabilities.' },
      { title: 'Flexible Models', description: 'Staff augmentation, dedicated teams, or project-based.' },
    ],
    pricingComparison: { local: '$15-35', morocco: '$25-40', savings: 'EU premium' },
    industries: [
      { name: 'Tourism & Hospitality', description: 'Booking platforms, travel apps, and hospitality management systems.' },
      { name: 'E-commerce', description: 'Cross-border retail platforms for European markets.' },
      { name: 'FinTech', description: 'Payment platforms and banking applications with EU compliance.' },
      { name: 'Healthcare', description: 'Telemedicine and health management platforms.' },
      { name: 'Real Estate', description: 'Property platforms for international buyers.' },
      { name: 'Logistics', description: 'Supply chain and shipping management systems.' },
    ],
    faqs: [
      { question: 'How does Morocco complement Thai tech teams?', answer: 'Morocco adds European languages (French, German, Italian), GDPR compliance, and CET timezone coverage — capabilities difficult to build in Thailand. This enables 24/7 follow-the-sun operations.' },
      { question: 'What is the timezone overlap?', answer: 'Morocco (GMT+1) is 6 hours behind Thailand (GMT+7). Teams share 3-4 hours of overlap daily, with handoff extending your productive window.' },
    ],
  }),

  malaysia: makeAgencyCountry({
    slug: 'software-development-agency-for-malaysia',
    countryName: 'Malaysia',
    countryFlag: '🇲🇾',
    timezone: 'MYT (GMT+8)',
    languages: ['English', 'French', 'Arabic', 'Malay'],
    currency: 'MYR / USD',
    title: 'Software Development Agency Malaysia 2026 – Call IT Dev',
    description: 'Software development agency for Malaysian companies. EU & MENA engineering, GDPR-compliant, multilingual. From $25/hr.',
    keywords: ['software development agency Malaysia', 'IT outsourcing Malaysia', 'nearshore development Malaysia', 'web development Kuala Lumpur'],
    heroTitle: 'Software Development Agency for Malaysia — EU & MENA Digital Partner',
    heroSubtitle: 'Malaysian companies going global: European engineering, Arabic capability, GDPR compliance. From $25/hr.',
    whyTitle: 'Why Malaysian Companies Choose Call IT Dev',
    whyIntro: 'Malaysia\'s tech sector expanding to Europe and MENA needs GDPR-compliant, multilingual engineering in the right timezone.',
    whyReasons: [
      { icon: '🌍', title: 'EU & MENA Markets', description: 'Native French, German, Arabic for European and Middle Eastern expansion.' },
      { icon: '⏰', title: 'Extended Coverage', description: 'CET (GMT+1) complements MYT (GMT+8) for follow-the-sun ops.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise-quality development.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'ISO 27001, EU data protection compliance.' },
      { icon: '🗣️', title: 'Arabic Capability', description: 'Native Arabic engineers for MENA market apps — unique advantage.' },
      { icon: '🏆', title: 'Quality', description: 'CI/CD, automated testing, code reviews.' },
    ],
    advantages: [
      { title: 'EU Gateway', description: 'Build products for European markets with native-language UX.' },
      { title: 'MENA Access', description: 'Arabic-first interfaces and RTL development expertise.' },
      { title: 'Follow-the-Sun', description: '7h timezone offset extends your development hours.' },
      { title: 'Compliance', description: 'GDPR, ISO 27001, PDPA familiarity.' },
      { title: 'Full-Stack', description: 'All modern technologies in-house.' },
      { title: 'Scale', description: 'Start with 2 devs, grow to 50+.' },
    ],
    pricingComparison: { local: '$15-30', morocco: '$25-40', savings: 'EU premium' },
    industries: [
      { name: 'FinTech & Islamic Finance', description: 'Shariah-compliant fintech platforms with Arabic interfaces.' },
      { name: 'E-commerce', description: 'Cross-border retail for EU and MENA markets.' },
      { name: 'Oil & Gas', description: 'Enterprise platforms for energy companies.' },
      { name: 'Healthcare', description: 'Telemedicine and health management systems.' },
      { name: 'Education', description: 'EdTech platforms for multilingual markets.' },
      { name: 'SaaS', description: 'Product development and scaling.' },
    ],
    faqs: [
      { question: 'Can you build Arabic interfaces for Malaysian Islamic finance?', answer: 'Absolutely. Our native Arabic engineers build RTL interfaces, Arabic NLP, and culturally-aware UX for Islamic finance and MENA markets — a unique capability.' },
      { question: 'How do timezone differences work?', answer: 'Morocco (GMT+1) is 7 hours behind Malaysia (GMT+8). We share 2-3 hours of overlap daily, with asynchronous handoff covering the rest.' },
    ],
  }),

  russia: makeAgencyCountry({
    slug: 'software-development-agency-for-russia',
    countryName: 'Russia',
    countryFlag: '🇷🇺',
    timezone: 'MSK (GMT+3)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'RUB / USD',
    title: 'Software Development Agency Russia 2026 – Call IT Dev',
    description: 'Software development agency for Russian companies. EU & MENA market expansion, GDPR-compliant, multilingual engineering. From $25/hr.',
    keywords: ['software development agency Russia', 'IT outsourcing Russia', 'nearshore development Russia', 'агентство разработки ПО'],
    heroTitle: 'Software Development Agency for Russia — EU & MENA Expansion Partner',
    heroSubtitle: 'Russian companies going global: native European and Arabic engineering, GDPR compliance, CET timezone. From $25/hr.',
    whyTitle: 'Why Russian Companies Partner with Call IT Dev',
    whyIntro: 'Russian businesses expanding internationally need EU-compliant, multilingual engineering for European and MENA markets.',
    whyReasons: [
      { icon: '🌍', title: 'EU & MENA Languages', description: 'Native French, German, Arabic, English — essential for Western expansion.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+1 is only 2h from Moscow (GMT+3). Real-time collaboration.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for EU-grade engineering.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Essential for EU market entry and compliance.' },
      { icon: '🗣️', title: 'Multilingual', description: '12+ European and MENA languages.' },
      { icon: '🏆', title: 'ISO 27001', description: 'Enterprise security certifications.' },
    ],
    advantages: [
      { title: 'European Market Entry', description: 'Build EU-compliant products with native-language UX from day one.' },
      { title: 'Close Timezone', description: '2h from Moscow — easy daily collaboration.' },
      { title: 'MENA Bridge', description: 'Arabic capability for Middle Eastern expansion.' },
      { title: 'Cost Effective', description: 'Competitive rates for enterprise quality.' },
      { title: 'Compliance', description: 'GDPR, ISO 27001, international standards.' },
      { title: 'Stability', description: 'Stable business environment with redundant infrastructure.' },
    ],
    pricingComparison: { local: '$20-50', morocco: '$25-40', savings: 'EU quality' },
    industries: [
      { name: 'IT & SaaS', description: 'Product engineering for Russian tech companies expanding to EU.' },
      { name: 'E-commerce', description: 'Cross-border retail platforms for European markets.' },
      { name: 'Energy', description: 'Enterprise platforms for energy companies.' },
      { name: 'FinTech', description: 'GDPR-compliant financial platforms.' },
      { name: 'Telecom', description: 'Customer-facing platforms for international operations.' },
      { name: 'Mining & Resources', description: 'Back-office and operational platforms.' },
    ],
    faqs: [
      { question: 'Can Morocco serve as an EU engineering base for Russian companies?', answer: 'Yes — we provide native EU languages, GDPR compliance, and CET timezone alignment that enable Russian companies to build and maintain EU-market-ready products.' },
    ],
  }),

  china: makeAgencyCountry({
    slug: 'software-development-agency-for-china',
    countryName: 'China',
    countryFlag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    languages: ['English', 'French', 'German', 'Arabic', 'Spanish'],
    currency: 'CNY / USD',
    title: 'Software Development Agency China 2026 – Call IT Dev',
    description: 'Software development agency for Chinese companies. EU & MENA market expansion, GDPR-compliant, multilingual. From $25/hr.',
    keywords: ['software development agency China', 'IT outsourcing China', '中国软件开发', 'nearshore development China'],
    heroTitle: 'Software Development Agency for China — EU & MENA Market Bridge',
    heroSubtitle: 'Chinese companies going global: native European and Arabic engineering, GDPR compliance, follow-the-sun coverage. From $25/hr.',
    whyTitle: 'Why Chinese Companies Choose Call IT Dev',
    whyIntro: 'Chinese enterprises expanding to European and MENA markets need local language capabilities, regulatory compliance, and timezone coverage that Morocco uniquely provides.',
    whyReasons: [
      { icon: '🌍', title: 'EU & MENA Languages', description: 'Native French, German, Spanish, Arabic, English — languages scarce in China.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'CET (GMT+1) complements CST (GMT+8) for 24/7 coverage.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for EU-standard engineering.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'Essential for EU market compliance and data protection.' },
      { icon: '📈', title: 'Scale', description: '200+ engineers for rapid international deployment.' },
      { icon: '🏆', title: 'Enterprise Quality', description: 'ISO 27001, CI/CD, 80%+ test coverage.' },
    ],
    advantages: [
      { title: 'European Gateway', description: 'Build EU-compliant products with native European UX.' },
      { title: 'MENA Access', description: 'Arabic-first interfaces for Middle East markets.' },
      { title: '24/7 Coverage', description: '7h offset enables follow-the-sun development.' },
      { title: 'Language Advantage', description: '5+ native European languages unavailable locally.' },
      { title: 'Compliance', description: 'GDPR, PCI-DSS, ISO 27001 for regulated markets.' },
      { title: 'Fast Deployment', description: 'Dedicated team in 2 weeks.' },
    ],
    pricingComparison: { local: '¥200-500', morocco: '¥170-270', savings: '35%+' },
    industries: [
      { name: 'E-commerce', description: 'European storefronts and marketplace platforms for Chinese sellers.' },
      { name: 'Tech & SaaS', description: 'EU-facing products for Chinese tech companies.' },
      { name: 'Automotive', description: 'Digital platforms for Chinese auto brands entering EU markets.' },
      { name: 'Telecom', description: 'Customer platforms for Chinese telecom operators in EMEA.' },
      { name: 'FinTech', description: 'GDPR-compliant financial platforms for EU operations.' },
      { name: 'Gaming', description: 'Multilingual player platforms for Chinese game publishers.' },
    ],
    faqs: [
      { question: 'How does Morocco help Chinese companies enter EU markets?', answer: 'We provide native European languages (French, German, Spanish), GDPR compliance, and CET timezone — capabilities extremely difficult to build in China but essential for EU success.' },
      { question: 'What is the timezone overlap?', answer: 'Morocco (GMT+1) is 7 hours behind China (GMT+8). This enables follow-the-sun development where code handoff doubles your productive hours.' },
    ],
  }),
};
